import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, MessageSquare, Clock, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/contact`)
      ]);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}`, { status });
      toast.success(`Appointment marked as ${status}`);
      fetchData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: 'Confirmed', className: 'bg-blue-100 text-blue-700' },
      attended: { label: 'Attended', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
      rescheduled: { label: 'Rescheduled', className: 'bg-yellow-100 text-yellow-700' },
    };
    const config = statusConfig[status] || statusConfig.confirmed;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filterStatus !== 'all' && apt.status !== filterStatus) return false;
    if (filterDate && apt.preferred_date !== filterDate) return false;
    return true;
  });

  const stats = {
    total: appointments.length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    attended: appointments.filter(a => a.status === 'attended').length,
    pending: appointments.filter(a => a.status === 'confirmed' && new Date(a.preferred_date) >= new Date()).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <main className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage appointments and messages</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Calendar className="w-8 h-8 text-cyan-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Confirmed</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.confirmed}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Attended</p>
                  <p className="text-3xl font-bold text-green-600">{stats.attended}</p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Messages</p>
                  <p className="text-3xl font-bold text-purple-600">{contacts.length}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="appointments" data-testid="appointments-tab">Appointments</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="contacts-tab">Messages</TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle>Appointments List</CardTitle>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="attended">Attended</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="rescheduled">Rescheduled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="w-[160px]"
                      data-testid="filter-date-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredAppointments.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No appointments found</p>
                  ) : (
                    filteredAppointments.map((appointment, index) => (
                      <Card key={appointment.id} className="border-2" data-testid={`appointment-item-${index}`}>
                        <CardContent className="p-6">
                          <div className="grid md:grid-cols-5 gap-4">
                            <div className="md:col-span-2">
                              <div className="font-semibold text-gray-900 mb-1">{appointment.patient_name}</div>
                              <div className="text-sm text-gray-600">{appointment.email}</div>
                              <div className="text-sm text-gray-600">{appointment.phone}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Service</div>
                              <div className="font-medium">{appointment.service_name}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Date & Time</div>
                              <div className="font-medium">{appointment.preferred_date}</div>
                              <div className="text-sm text-gray-600">{appointment.preferred_time}</div>
                            </div>
                            <div className="flex flex-col gap-2">
                              {getStatusBadge(appointment.status)}
                              {appointment.status === 'confirmed' && (
                                <Select
                                  onValueChange={(value) => updateAppointmentStatus(appointment.id, value)}
                                >
                                  <SelectTrigger className="w-full text-xs" data-testid={`status-update-${index}`}>
                                    <SelectValue placeholder="Update" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="attended">Mark Attended</SelectItem>
                                    <SelectItem value="cancelled">Cancel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                          {appointment.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-sm text-gray-600 mb-1">Notes:</div>
                              <div className="text-sm text-gray-900">{appointment.notes}</div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <CardTitle>Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No messages found</p>
                  ) : (
                    contacts.map((contact, index) => (
                      <Card key={contact.id} className="border-2" data-testid={`contact-item-${index}`}>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <div className="font-semibold text-gray-900 mb-1">{contact.name}</div>
                              <div className="text-sm text-gray-600">{contact.email}</div>
                              <div className="text-sm text-gray-600">{contact.phone}</div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {contact.created_at && format(new Date(contact.created_at), 'PPp')}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-900">{contact.message}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default AdminDashboard;
